import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { STATUS_LABEL_RU } from '@wit-support/ui-domain';
import { toast } from '@wit-support/ui';
import { ConversationMergedError, updateConversationStatus } from '../api.js';
/**
 * localStorage key for the last-open conversation. Lives here (not in
 * `inbox-page.tsx`) so the status mutation below can forget a thread the
 * operator just wrapped up — and `inbox-page.tsx` (plus the admin-web shell,
 * via the module's client barrel) imports it from here, keeping a single
 * definition.
 */
export const INBOX_LAST_CONVERSATION_KEY = 'wit-admin:inbox-last-conversation';
/**
 * localStorage key for the persisted inbox filter/search state. Re-exported
 * from the module's client barrel so `app-shell.tsx` (sign-out cleanup) and
 * `inbox-page.tsx` (read/write) share one definition instead of each keeping a
 * private copy of the string.
 */
export const INBOX_FILTERS_KEY = 'wit-admin:inbox-filters';
/**
 * SINGLE source for "change a conversation's status" from the admin UI:
 * `updateConversationStatus` PATCH + success toast + cache invalidation
 * (`['conversation', id]` and `['conversations']`) + forgetting the remembered
 * last-open chat on an explicit close (`resolved`/`closed`) + merge-redirect
 * handling. Consumed by BOTH the thread-header `StatusControl` and the post-send
 * `StatusAfterSendDialog`, so the two can never drift (they previously had two
 * copies, one of which silently dropped the last-open cleanup).
 *
 * `onChanged` fires only on a successful status change — callers use it to close
 * a dialog. It does NOT fire on error (so a dialog stays open for retry) nor on
 * the merge-redirect branch (which navigates away itself).
 *
 * The mutation variable is the whole {@link UpdateConversationRequest}, not a
 * bare status: a manual change may carry the operator's note (`comment`), which
 * the server stores as an internal comment linked to the status-history row.
 * Callers that have no note simply pass `{ status }`.
 */
export function useConversationStatusMutation(conversationId, onChanged) {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    return useMutation({
        mutationFn: (next) => updateConversationStatus(conversationId, next),
        onSuccess: (updated) => {
            toast.success(`Статус изменён: ${STATUS_LABEL_RU[updated.status]}`);
            void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
            void queryClient.invalidateQueries({ queryKey: ['conversations'] });
            // История статусов живёт под СВОИМ префиксом, а не под `['conversation',
            // id, …]`, поэтому инвалидация выше её не задевает. Смена статуса — ровно
            // то событие, ради которого её открывают, и оставить её протухшей значит
            // показать оператору историю без только что сделанной им записи.
            // (Лента комментариев — `['conversation', id, 'comments']` — уже попала
            // под префиксную инвалидацию строкой выше, отдельный вызов ей не нужен.)
            void queryClient.invalidateQueries({ queryKey: ['status-history', conversationId] });
            // Finishing a conversation (resolved/closed) is an explicit close — forget
            // it as the remembered open chat so returning to the inbox does not
            // re-open a thread the operator deliberately wrapped up (matches
            // back-to-list / blacklist behaviour).
            if (updated.status === 'closed' || updated.status === 'resolved') {
                try {
                    localStorage.removeItem(INBOX_LAST_CONVERSATION_KEY);
                }
                catch {
                    /* best-effort */
                }
            }
            onChanged?.();
        },
        onError: (err) => {
            if (err instanceof ConversationMergedError) {
                if (err.targetId) {
                    toast.success('Эта переписка была объединена — открываю актуальную');
                    void navigate({
                        to: '/conversations/$conversationId',
                        params: { conversationId: err.targetId },
                    });
                }
                else {
                    toast.error('Эта переписка была объединена — обновите страницу');
                }
                return;
            }
            toast.error(err.message);
        },
    });
}
//# sourceMappingURL=use-conversation-status-mutation.js.map